import { useAuthStore } from '../store/authStore'
import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Send, Bot, User, Sparkles, Globe, ArrowDown,
  Trash2, Plus, MessageSquare, AlertTriangle,
  Copy, RefreshCw, Paperclip, X, FileText, Image, Edit3, Archive,
  Mic, Volume2, StopCircle, Download, Share2, Upload,
  Settings, CheckSquare, Square, FileDown,
  Camera, Link
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import DOMPurify from 'dompurify'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { ExternalLink, Copy as CopyIcon } from 'lucide-react'
import { useChatStore } from '../store/chatStore'
import { getProviderIcon, getProviderColor } from '../utils/modelIcons'
import toast from 'react-hot-toast'
import { useTokenStore } from '../store/tokenStore'
import client from '../api/client'
import { exportChats, importChatsFromFile, copyShareLink, shareToSocial } from '../utils/chatExport'
import type { Chat as ChatType } from '../types'
import ChatSettingsPanel, { loadChatSettings, applyChatSettings } from './ChatSettings'

// Компонент для блока кода с кнопкой копирования
const CodeBlock = ({ language, value }: { language?: string; value: string }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="relative group my-3 rounded-xl overflow-hidden border border-white/5">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-1.5 bg-[#282c34] border-b border-white/5">
        <span className="text-[11px] text-gray-400 font-mono">
          {language || 'code'}
        </span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] text-gray-400 hover:text-gray-200 hover:bg-white/5 transition-all"
        >
          <CopyIcon className="w-3 h-3" />
          {copied ? 'Скопировано!' : 'Копировать'}
        </button>
      </div>
      <SyntaxHighlighter
        style={oneDark}
        language={language || 'text'}
        PreTag="div"
        customStyle={{
          margin: 0,
          padding: '1rem',
          fontSize: '0.8rem',
          lineHeight: '1.5',
          background: '#1e1e2e',
        }}
        showLineNumbers={false}
        wrapLines
      >
        {value}
      </SyntaxHighlighter>
    </div>
  )
}

// Кастомные компоненты для ReactMarkdown
const markdownComponents = {
  // Таблицы
  table: ({ children }: any) => (
    <div className="overflow-x-auto my-3 rounded-xl border border-white/5">
      <table className="min-w-full divide-y divide-white/10 text-sm">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }: any) => (
    <thead className="bg-indigo-500/10">{children}</thead>
  ),
  th: ({ children }: any) => (
    <th className="px-4 py-2.5 text-left text-xs font-semibold text-indigo-300 uppercase tracking-wider whitespace-nowrap">
      {children}
    </th>
  ),
  td: ({ children }: any) => (
    <td className="px-4 py-2.5 text-xs text-gray-300 border-t border-white/5 whitespace-nowrap">
      {children}
    </td>
  ),
  // Блоки кода
  code: ({ className, children, ...props }: any) => {
    const match = /language-(\w+)/.exec(className || '')
    const codeString = String(children).replace(/\n$/, '')
    
    if (match) {
      return <CodeBlock language={match[1]} value={codeString} />
    }
    
    // Inline code
    return (
      <code className="px-1.5 py-0.5 rounded-md bg-indigo-500/10 text-indigo-300 text-xs font-mono" {...props}>
        {children}
      </code>
    )
  },
  // Inline code (fallback)
  inlineCode: ({ children }: any) => (
    <code className="px-1.5 py-0.5 rounded-md bg-indigo-500/10 text-indigo-300 text-xs font-mono">
      {children}
    </code>
  ),
  // Ссылки — открывать в новой вкладке
  a: ({ href, children }: any) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2 decoration-indigo-500/30">
      {children}
    </a>
  ),
}

// Препроцессор текста перед markdown: конвертирует математические формулы
// в нотацию, понятную KaTeX.
// - Заменяет [ ... ] на $$ ... $$ (display) если внутри есть мат. символы
// - Заменяет ( ... ) на $ ... $ (inline) если внутри похоже на переменную
function preprocessMath(text: string): string {
  const MATH_CHARS = /[\\{}_^∫∂∑∏π∞αβγδεζηθικλμνξοπρστυφχψωΓΔΘΛΞΠΣΦΨΩℏ∇∈∀∃±×÷≤≥≈≠√∛∜∝∞∧∨⊕⊗†‡·°′″‴⁄←↑→↓↔↕↖↗↘↙↩↪↼↽↾↿⇀⇁⇂⇃⇄⇆⇇⇈⇉⇊⇋⇌⇐⇑⇒⇓⇔⇕⇖⇗⇘⇙⇚⇛⇜⇝⇞⇟⇠⇡⇢⇣⇤⇥⇦⇧⇨⇩⇪]/u

  let result = text

  // 0. Конвертируем \[ ... \] (LaTeX display) в $$ ... $$
  result = result.replace(/\\\[([\s\S]*?)\\\]/g, (_, inner) => `\$\$${inner.trim()}\$\$`)
  // Конвертируем \( ... \) (LaTeX inline) в $ ... $
  result = result.replace(/\\\(([\s\S]*?)\\\)/g, (_, inner) => `\$${inner.trim()}\$`)

  // Сначала защитим markdown-ссылки: заменим их на плейсхолдеры
  const mdLinks: string[] = []
  result = result.replace(/\[([^\]]+)\]\(https?:\/[^)]+\)/g, (match) => {
    mdLinks.push(match)
    return `%%MDLINK${mdLinks.length - 1}%%`
  })

  // Защитим обычные URL
  const urls: string[] = []
  result = result.replace(/https?:\/\/[^\s.,;:!?)\]}]+/g, (match) => {
    urls.push(match)
    return `%%URL${urls.length - 1}%%`
  })

  // Защитим текстовые ссылки [n] (цитаты источников)
  result = result.replace(/\[\s*\d+\s*\]/g, (match) => {
    urls.push(match)
    return `%%URL${urls.length - 1}%%`
  })

  // Теперь конвертируем display math [ ... ]
  // Блок [ ... ] на отдельной строке или внутри текста с мат. символами
  result = result.replace(/\[([^\]\n]+?)\]/g, (match, inner) => {
    const trimmed = inner.trim()
    // Если внутри есть математические символы — это формула
    if (MATH_CHARS.test(trimmed) || /^[a-zA-Z0-9_+\-*/^=<>~]+$/.test(trimmed) === false && trimmed.length > 2) {
      // Если содержит \, _ или ^ — почти наверняка формула
      if (/[\\_^]/.test(trimmed) || MATH_CHARS.test(trimmed)) {
        return `$$${trimmed}$$`
      }
    }
    return match // не трогаем
  })

  // Конвертируем inline math ( ... ) с мат. символами
  result = result.replace(/\(([^)]{1,80})\)/g, (match, inner) => {
    const trimmed = inner.trim()
    if (/[\\_{}α-ωΓ-Ω∫∂∑∏π∞∇]/.test(trimmed) && !trimmed.includes('http')) {
      return `$${trimmed}$`
    }
    return match
  })

  // Восстанавливаем URL
  result = result.replace(/%%MDLINK(\d+)%%/g, (_, idx) => mdLinks[parseInt(idx)] || '')
  result = result.replace(/%%URL(\d+)%%/g, (_, idx) => urls[parseInt(idx)] || '')

  // Нормализация существующих $...$ и $$...$$ блоков:
  // remark-math требует $ без пробелов на границах
  result = result.replace(/\$\$\s+/g, '$$').replace(/\s+\$\$/g, '$$')
  // Убираем пробелы после $ и перед $, но не трогаем $$ 
  // Разбиваем на блоки по $$ и обрабатываем каждый
  const parts: string[] = []
  let lastIdx = 0
  let idx: number
  while ((idx = result.indexOf('$$', lastIdx)) !== -1) {
    parts.push(result.slice(lastIdx, idx))
    const endIdx = result.indexOf('$$', idx + 2)
    if (endIdx === -1) {
      parts.push(result.slice(idx))
      lastIdx = result.length
      break
    }
    parts.push(result.slice(idx, endIdx + 2))
    lastIdx = endIdx + 2
  }
  if (lastIdx < result.length) parts.push(result.slice(lastIdx))
  
  result = parts.map(part => {
    // Вне $$ блоков — нормализуем $...$ (не $$)
    if (!part.startsWith('$$')) {
      // Заменяем $ с пробелами на $ без пробелов, осторожно чтобы не задеть $$ 
      let out = ''
      let i = 0
      while (i < part.length) {
        if (part[i] === '$' && (i + 1 >= part.length || part[i + 1] !== '$')) {
          // Одиночный $ — убираем пробел после него
          out += '$'
          i++
          while (i < part.length && (part[i] === ' ' || part[i] === '\t' || part[i] === '\n')) i++
        } else if (part[i] === '$') {
          out += '$$'
          i += 2
        } else {
          out += part[i]
          i++
        }
      }
      return out
    }
    return part
  }).join('')

  // Убираем переносы строк внутри $$...$$ блоков
  // (разбиваем на блоки, не начинающиеся с $$, и остальное)
  result = result.replace(/\$\$([\s\S]*?)\$\$/g, (_, inner) => `\$\$${inner.replace(/\s+/g, ' ')}\$\$`)

  return result
}

// Компонент для сообщения ассистента с выделением источников
const AssistantMessage = ({ content }: { content: string }) => {
  const parts = useMemo(() => {
    let text = content || ''
    // Препроцессор: математические формулы
    text = preprocessMath(text)
    // Разделяем элементы с маркерами (✔, 🔹, ✅, ☑️, •, -, *) где они склеены в одну строку
    // Сначала: если маркер идёт не в начале строки — ставим перенос перед ним
    text = text.replace(/(?:^|[.]\s*)?([✔🔹✅☑️•💧🔬🚀⭐🌟💫✨])\s*/gm, '\n$1 ')
    // Убираем двойные переносы
    text = text.replace(/\n{2,}/g, '\n')
    text = text.trim()
    // Преобразуем строки в markdown-списки:
    // - строки с эмодзи в начале → bullet points
    // - строки без эмодзи и без маркеров → нумерованный список
    // Разбиваем по нумерованным пунктам "n. Текст" в середине строки
    text = text.replace(/(\d+)\.\s+(?=\S)/g, (match, num) => {
      return parseInt(num) > 0 ? '\n' + match : match
    })
    // Разбиваем по "- " или "- " в середине строки (склеенные bullet points)
    text = text.replace(/(?<!^)-\s+(?=\S)/gm, '\n- ')
    // Разбиваем по "* " в середине строки
    text = text.replace(/(?<!^)\*\s+(?=\S)/gm, '\n* ')
    const lines = text.split('\n').filter(l => l.trim())
    let listMode: 'bullet' | 'ordered' | null = null
    let orderedCounter = 0
    text = lines.map(line => {
      const t = line.trim()
      if (/^[✔🔹✅☑️•💧🔬🚀⭐🌟💫✨]/.test(t)) {
        if (listMode !== 'bullet') { listMode = 'bullet'; orderedCounter = 0 }
        return '- ' + t
      }
      if (/^\s*[-*+]\s/.test(t)) { listMode = 'bullet'; return line }
      if (/^\d+\.\s/.test(t)) { listMode = 'ordered'; return line }
      if (t && !t.startsWith('- ') && !t.startsWith('* ') && !/^\d+\.\s/.test(t)) {
        if (listMode === null) { listMode = 'ordered'; orderedCounter = 0 }
        if (listMode === 'ordered') { orderedCounter++; return orderedCounter + '. ' + t }
        return '- ' + t
      }
      return line
    }).join('\n')
    const urlRegex = /https?:\/\/[^\s\])}]+/g
    const matches: string[] = []
    let m
    while ((m = urlRegex.exec(text)) !== null) {
      matches.push(m[0])
    }

    // Если есть ссылки — отделяем их от основного контента
    if (matches.length === 0) {
      return { main: text, sources: [] as string[] }
    }

    // Удаляем ссылки из основного контента
    let main = text
    const sources: string[] = []
    for (const url of matches) {
      if (!sources.includes(url)) {
        sources.push(url)
      }
      main = main.replace(url, '')
    }
    // Удаляем также markdown-ссылки [text](url) и цитаты ссылок
    main = main.replace(/\s*\[\s*\d+\s*\]\s*/g, '')
    main = main.replace(/\(https?:\/[^)]+\)/g, '')
    main = main.replace(/\[([^\]]+)\]\(https?:\/[^)]+\)/g, '$1')
    main = main.replace(/https?:\/[^\s.,;:!?)\]}]+/g, '')
    main = main.replace(/\n{3,}/g, '\n\n').trim()

    return { main, sources }
  }, [content])

  const markdownContent = parts.sources.length === 0 ? preprocessMath(content) : parts.main

  return (
    <div className="chat-message-content">
      <div className="prose prose-invert max-w-none">
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[rehypeKatex]}
          components={markdownComponents}
        >
          {DOMPurify.sanitize(markdownContent)}
        </ReactMarkdown>
      </div>
      {parts.sources.length > 0 && (
        <div className="mt-4 pt-3 border-t border-yellow-500/20">
          <div className="text-xs font-semibold text-yellow-400 mb-2">📎 Источники:</div>
          <div className="space-y-1">
            {parts.sources.map((url, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-yellow-400 text-xs font-mono mt-0.5 shrink-0">{i + 1}.</span>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-yellow-300/80 hover:text-yellow-200 break-all underline underline-offset-2 decoration-yellow-500/30 hover:decoration-yellow-400/60 transition-colors"
                >
                  {url}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// 13+ новых моделей с провайдерами
// Тип для модели (из БД)
interface AiModelItem {
  id: number
  model_name: string
  display_name: string | null
  provider: string | null
  category: string
  is_paid: boolean
  icon_url: string | null
  is_active: boolean
  cost_per_million_usd: number | null
}

// Хардкодные цены чат-моделей (прямо как в backend/config.py)
const CHAT_MODEL_PRICES: Record<string, {input: number, output: number}> = {
  'openai/gpt-5.5': {input: 12, output: 58},
  'openai/gpt-5.5-pro': {input: 70, output: 350},
  'openai/gpt-5.4': {input: 6, output: 29},
  'openai/gpt-5.4-nano': {input: 0, output: 2},
  'openai/gpt-5.4-mini': {input: 2, output: 9},
  'openai/gpt-5.3-chat': {input: 5, output: 26},
  'openai/gpt-5.2-pro': {input: 63, output: 315},
  'openai/gpt-5.2': {input: 5, output: 26},
  'openai/gpt-5.1': {input: 4, output: 18},
  'openai/gpt-5': {input: 4, output: 18},
  'openai/gpt-5-mini': {input: 1, output: 3},
  'openai/gpt-5-chat': {input: 4, output: 18},
  'openai/gpt-audio': {input: 4, output: 22},
  'openai/gpt-audio-mini': {input: 1, output: 5},
  'deepseek/deepseek-v4-pro': {input: 0, output: 2},
  'deepseek/deepseek-v4-flash': {input: 0, output: 1},
  'deepseek/deepseek-chat': {input: 0, output: 2},
  'deepseek/deepseek-r1': {input: 1, output: 7},
  'anthropic/claude-opus-4.8-fast': {input: 20, output: 100},
  'anthropic/claude-opus-4.8': {input: 10, output: 50},
  'anthropic/claude-opus-4.7-fast': {input: 60, output: 300},
  'anthropic/claude-opus-4.7': {input: 10, output: 50},
  'anthropic/claude-opus-4.6-fast': {input: 60, output: 300},
  'anthropic/claude-opus-4.6': {input: 10, output: 50},
  'anthropic/claude-opus-4.5': {input: 10, output: 50},
  'anthropic/claude-opus-4': {input: 30, output: 150},
  'anthropic/claude-opus-4.1': {input: 30, output: 150},
  'anthropic/claude-sonnet-4.6': {input: 6, output: 30},
  'anthropic/claude-sonnet-4.5': {input: 6, output: 30},
  'anthropic/claude-sonnet-4': {input: 6, output: 30},
  'anthropic/claude-haiku-4.5': {input: 2, output: 10},
  'google/gemini-3.5-flash': {input: 4, output: 18},
  'google/gemini-3.1-flash-lite': {input: 1, output: 3},
  'google/gemini-3.1-pro-preview': {input: 5, output: 23},
  'google/gemini-2.5-flash': {input: 1, output: 5},
  'x-ai/grok-4.3': {input: 1, output: 6},
  'x-ai/grok-build-0.1': {input: 1, output: 5},
  'qwen/qwen3.7-max': {input: 2, output: 8},
  'qwen/qwen3.7-plus': {input: 1, output: 3},
  'meta-llama/llama-4-scout': {input: 1, output: 3},
  'meta-llama/llama-4-maverick': {input: 3, output: 13},
  'mistralai/mistral-large-2512': {input: 2, output: 10},
}

// Конвертирует модель из БД в формат для селектора
const toSelectorItem = (m: AiModelItem) => {
  // Определяем цену: сначала пытаемся рассчитать из cost_per_million_usd, потом из хардкода
  let priceText = ''
  if (m.is_paid) {
    let inputPrice = 0
    let outputPrice = 0
    if (m.cost_per_million_usd && m.cost_per_million_usd > 0) {
      inputPrice = Math.round(m.cost_per_million_usd / 6 / 1000000 * 2 * 1000000)
      outputPrice = Math.max(1, Math.round(m.cost_per_million_usd * 5 / 6 / 1000000 * 2 * 1000000))
    } else {
      const hardcoded = CHAT_MODEL_PRICES[m.model_name]
      if (hardcoded) {
        inputPrice = hardcoded.input
        outputPrice = hardcoded.output
      }
    }
    if (outputPrice > 0) {
      priceText = `${inputPrice}/${outputPrice}`
    }
  }
  return {
    id: m.model_name,
    name: m.display_name || m.model_name,
    provider: m.provider === 'typegpt' ? 'GPT-Chatbot' : (m.provider || m.category || 'AI'),
    icon: m.is_paid ? '👑' : 'FREE',
    color: m.is_paid ? 'text-indigo-400' : 'text-emerald-400',
    priceText,
    is_paid: m.is_paid,
  }
}

interface AttachedFile {
  name: string
  base64: string
  type: string
}

export default function Chat() {
  const { id } = useParams()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const [input, setInput] = useState('')
  const [selectedModel, setSelectedModel] = useState('')
  const [webSearch, setWebSearch] = useState(false)
  const [deepResearch, setDeepResearch] = useState(false)
  const [showDeepResearchPopup, setShowDeepResearchPopup] = useState(false)
  const [showModelSelector, setShowModelSelector] = useState(false)
  const [hoveredMsgId, setHoveredMsgId] = useState<string | null>(null)
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([])
  const [editMessageId, setEditMessageId] = useState<string | null>(null)
  const [editMessageContent, setEditMessageContent] = useState<string>('')
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [speakingMsgId, setSpeakingMsgId] = useState<string | null>(null)
  const [showVoiceMenu, setShowVoiceMenu] = useState(false)
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([])
  const [selectedVoiceName, setSelectedVoiceName] = useState<string | null>(null)
  const recognitionRef = useRef<any>(null)
  // Voice dialog state
  const [showVoicePopup, setShowVoicePopup] = useState(false)
  const [voiceDialogOpen, setVoiceDialogOpen] = useState(false)
  const [voiceDialogStatus, setVoiceDialogStatus] = useState<'idle' | 'listening' | 'thinking' | 'speaking'>('idle')
  const [voiceDialogTranscript, setVoiceDialogTranscript] = useState<{role: 'user' | 'assistant', text: string}[]>([])
  const voiceDialogTranscriptRef = useRef<{role: 'user' | 'assistant', text: string}[]>([])

  // Экспорт/импорт/шэринг
  const [selectMode, setSelectMode] = useState(false)
  const [selectedChatIds, setSelectedChatIds] = useState<Set<string>>(new Set())
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [showShareMenu, setShowShareMenu] = useState<string | null>(null)
  const [showSettingsPanel, setShowSettingsPanel] = useState(false)
  const [showAttachMenu, setShowAttachMenu] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const fileImportRef = useRef<HTMLInputElement>(null)

  // Загрузка активных моделей с бэка (учитывает is_active из админки)
  const [serverModels, setServerModels] = useState<AiModelItem[]>([])
  const [serverModelsLoading, setServerModelsLoading] = useState(true)
  
  // Мапа model_name → { icon_url, display_name } для быстрого поиска в UI
  const aiModels = useMemo(() => {
    const map: Record<string, { icon_url: string | null; display_name: string | null }> = {}
    serverModels.forEach(m => { map[m.model_name] = { icon_url: m.icon_url, display_name: m.display_name } })
    return map
  }, [serverModels])
  
  // Загрузка настроек моделей из профиля
  const [userModels, setUserModels] = useState<string[] | null>(null)
  
  // Загрузка AI моделей с бэка (только is_active == true)
  useEffect(() => {
    Promise.all([
      client.get('/models/ai-models'),
      client.get('/models/my'),
    ]).then(([modelsResp, myResp]) => {
      const items: AiModelItem[] = (modelsResp.data || []).filter((m: AiModelItem) => m.is_active && m.category === 'chat')
      setServerModels(items)
      setServerModelsLoading(false)
      
      const enabled: string[] = myResp.data?.chat_models
      if (enabled && enabled.length > 0) {
        setUserModels(enabled)
      }
      // Модель по умолчанию — первая бесплатная из списка (приоритет enabled)
      const sortedByPaid = [...items].sort((a, b) => (a.is_paid ? 1 : 0) - (b.is_paid ? 1 : 0))
      const defaultModel = enabled && enabled.length > 0
        ? sortedByPaid.find(m => enabled.includes(m.model_name))
        : sortedByPaid[0]
      if (defaultModel) setSelectedModel(defaultModel.model_name)
    }).catch(() => {
      setServerModelsLoading(false)
    })
  }, [])

  // Инициализация настроек чата
  useEffect(() => {
    applyChatSettings(loadChatSettings())
  }, [])

  // Resizable sidebar
  const sidebarRef = useRef<HTMLDivElement | null>(null)

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const sidebar = document.getElementById('chat-sidebar')
    if (!sidebar) return

    const startX = e.clientX
    const startWidth = sidebar.offsetWidth

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX
      const newWidth = Math.max(200, Math.min(500, startWidth + delta))
      sidebar.style.width = `${newWidth}px`
    }

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [])

  // Загрузка голосов при монтировании
  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices()
      if (voices.length > 0) {
        // Приоритет: Microsoft Neural > Google Neural > Apple > остальные русские
        const ranked = voices
          .filter(v => v.lang.startsWith('ru'))
          .sort((a, b) => {
            const aScore = voicePriority(a)
            const bScore = voicePriority(b)
            return bScore - aScore
          })
        setAvailableVoices(ranked)

        // Автовыбор лучшего голоса, если ещё не выбран
        if (!selectedVoiceName && ranked.length > 0) {
          setSelectedVoiceName(ranked[0].name)
        }
      }
    }

    loadVoices()
    window.speechSynthesis.onvoiceschanged = loadVoices
    return () => { window.speechSynthesis.onvoiceschanged = null }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Приоритет голоса (чем выше число, тем лучше)
  const voicePriority = (v: SpeechSynthesisVoice): number => {
    const name = v.name.toLowerCase()
    const lang = v.lang.toLowerCase()
    if (lang.startsWith('ru')) {
      // Microsoft Neural — лучшие
      if (name.includes('microsoft') && (name.includes('neural') || name.includes('natural'))) return 100
      // Google Neural / Wavenet
      if (name.includes('google') && (name.includes('neural') || name.includes('wavenet'))) return 90
      // Apple (Siri, enhanced)
      if (name.includes('apple') || name.includes('samantha') || name.includes('milena')) return 80
      // Яндекс, Yandex
      if (name.includes('yandex') || name.includes('яндекс')) return 75
      // Microsoft обычные
      if (name.includes('microsoft')) return 70
      // Google обычные
      if (name.includes('google')) return 60
      // Остальные русские
      return 50
    }
    return 0
  }

  // Получение выбранного голоса
  const getSelectedVoice = useCallback((): SpeechSynthesisVoice | null => {
    if (selectedVoiceName) {
      return availableVoices.find(v => v.name === selectedVoiceName) || null
    }
    return availableVoices[0] || null
  }, [selectedVoiceName, availableVoices])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Голосовой ввод (Speech-to-Text)
  const startRecording = useCallback(() => {
    if (isRecording) {
      // Остановить запись
      if (recognitionRef.current) {
        recognitionRef.current.stop()
      }
      setIsRecording(false)
      return
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SpeechRecognition) {
      toast.error('Ваш браузер не поддерживает голосовой ввод')
      return
    }

    const recognition = new SpeechRecognition()
    recognition.lang = 'ru-RU'
    recognition.continuous = false
    recognition.interimResults = true
    recognition.maxAlternatives = 1

    recognition.onresult = (event: any) => {
      // Берём только последний финальный результат
      const results = event.results
      const lastResult = results[results.length - 1]
      if (lastResult && lastResult.isFinal) {
        const text = lastResult[0].transcript
        setInput(text)
      }
    }

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error)
      if (event.error !== 'aborted') {
        toast.error('Ошибка распознавания речи')
      }
      setIsRecording(false)
    }

    recognition.onend = () => {
      setIsRecording(false)
    }

    recognitionRef.current = recognition
    recognition.start()
    setIsRecording(true)
  }, [isRecording])

  // --- Voice Dialog (continuous speech mode) ---
  const voiceDialogRecognitionRef = useRef<any>(null)
  const voiceDialogAudioRef = useRef<HTMLAudioElement | null>(null)
  const voiceDialogActiveRef = useRef(false)
  // Refs for useChatStore values (avoids closure ordering issues — assigned after hooks below)
  const voiceDialogCurrentChatRef = useRef<any>(null)
  const voiceDialogSendMsgStreamRef = useRef<any>(null)
  const voiceDialogSelectedModelRef = useRef(selectedModel)

  const voiceDialogListen = useCallback(() => {
    if (!voiceDialogActiveRef.current) return

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SpeechRecognition) {
      toast.error('Ваш браузер не поддерживает голосовой ввод')
      setVoiceDialogOpen(false)
      return
    }

    const recognition = new SpeechRecognition()
    recognition.lang = 'ru-RU'
    recognition.continuous = false
    recognition.interimResults = true
    recognition.maxAlternatives = 1

    recognition.onresult = async (event: any) => {
      const results = event.results
      const lastResult = results[results.length - 1]
      if (lastResult && lastResult.isFinal && voiceDialogActiveRef.current) {
        const text = lastResult[0].transcript
        if (!text.trim()) {
          // Ничего не распознано — слушаем снова
          if (voiceDialogActiveRef.current) {
            voiceDialogListen()
          }
          return
        }

        // Добавляем в транскрипт
        setVoiceDialogTranscript(prev => { const n = [...prev, { role: 'user' as const, text }]; voiceDialogTranscriptRef.current = n; return n })
        setVoiceDialogStatus('thinking')

        try {
          const res = await client.post('/voice/chat-and-speak', {
            text,
            model: selectedModel,
          })
          const data = res.data
          const aiText = data.text || ''
          const cleanText = data.clean_text || aiText

          // Добавляем ответ в транскрипт
          setVoiceDialogTranscript(prev => { const n = [...prev, { role: 'assistant' as const, text: aiText }]; voiceDialogTranscriptRef.current = n; return n })
          setVoiceDialogStatus('speaking')

          // Озвучиваем ответ через TTS
          try {
            const ttsRes = await client.post('/tts/synthesize', {
              text: cleanText.slice(0, 2000),
              voice: 'ru',
            }, { responseType: 'blob' })
            const audioUrl = URL.createObjectURL(ttsRes.data)
            const audio = new Audio(audioUrl)
            voiceDialogAudioRef.current = audio

            audio.onended = () => {
              URL.revokeObjectURL(audioUrl)
              voiceDialogAudioRef.current = null
              // После окончания озвучки — снова слушаем
              if (voiceDialogActiveRef.current) {
                setVoiceDialogStatus('listening')
                voiceDialogListen()
              }
            }
            audio.play().catch(err => {
              console.error('Voice dialog audio play error:', err)
              if (voiceDialogActiveRef.current) {
                setVoiceDialogStatus('listening')
                voiceDialogListen()
              }
            })
          } catch (ttsErr) {
            console.error('TTS error:', ttsErr)
            // Если TTS не сработал — просто слушаем дальше
            if (voiceDialogActiveRef.current) {
              setVoiceDialogStatus('listening')
              voiceDialogListen()
            }
          }
        } catch (err) {
          console.error('Voice chat error:', err)
          setVoiceDialogTranscript(prev => [...prev, { role: 'assistant', text: 'Ошибка при получении ответа. Попробуйте ещё раз.' }])
          if (voiceDialogActiveRef.current) {
            setVoiceDialogStatus('listening')
            voiceDialogListen()
          }
        }
      }
    }

    recognition.onerror = (event: any) => {
      console.log('[VoiceDialog] Recognition error:', event.error, 'active:', voiceDialogActiveRef.current)
      // Повторяем только если в режиме прослушивания
      if (voiceDialogActiveRef.current && voiceDialogStatus === 'listening') {
        setTimeout(() => {
          if (voiceDialogActiveRef.current && voiceDialogStatus === 'listening') {
            console.log('[VoiceDialog] Retrying after error...')
            voiceDialogListen()
          }
        }, 300)
      }
    }

    recognition.onend = () => {
      console.log('[VoiceDialog] Recognition ended, active:', voiceDialogActiveRef.current, 'status:', voiceDialogStatus)
      // Запускаем заново только если нет активных операций (thinking/speaking)
      if (voiceDialogActiveRef.current && voiceDialogStatus === 'listening') {
        setTimeout(() => {
          if (voiceDialogActiveRef.current && voiceDialogStatus === 'listening') {
            console.log('[VoiceDialog] Restarting recognition...')
            voiceDialogListen()
          }
        }, 200)
      }
    }

    voiceDialogRecognitionRef.current = recognition
    try {
      console.log('[VoiceDialog] Starting recognition...')
      recognition.start()
      console.log('[VoiceDialog] Recognition started OK')
    } catch (e) {
      console.error('[VoiceDialog] Failed to start recognition:', e)
    }
  }, [selectedModel])

  const startVoiceDialog = useCallback(() => {
    setShowVoicePopup(false)
    setVoiceDialogTranscript([])
    setVoiceDialogStatus('listening')
    setVoiceDialogOpen(true)
    voiceDialogActiveRef.current = true
    // Небольшая задержка для рендера
    setTimeout(() => {
      voiceDialogListen()
    }, 1000)
  }, [voiceDialogListen])

  const stopVoiceDialog = useCallback(() => {
    voiceDialogActiveRef.current = false
    if (voiceDialogRecognitionRef.current) {
      try {
        voiceDialogRecognitionRef.current.stop()
      } catch {}
    }
    if (voiceDialogAudioRef.current) {
      voiceDialogAudioRef.current.pause()
      voiceDialogAudioRef.current = null
    }
    setVoiceDialogStatus('idle')
    setVoiceDialogOpen(false)
  }, [])

  // Сохранить диалог в чат
  const saveVoiceDialogToChat = useCallback(async () => {
    const transcript = voiceDialogTranscriptRef.current
    if (transcript.length === 0) return

    const chat = voiceDialogCurrentChatRef.current
    const sendStream = voiceDialogSendMsgStreamRef.current
    const model = voiceDialogSelectedModelRef.current

    if (!chat || !sendStream) return

    const messagesToSend = transcript.filter(m => m.role === 'user')
    const combinedText = messagesToSend.map(m => m.text).join('\n')
    if (combinedText.trim()) {
      const controller = new AbortController()
      abortControllerRef.current = controller
      await sendStream(chat.id, combinedText, controller.signal, model, false, [], false)
    }
    stopVoiceDialog()
  }, [stopVoiceDialog])

  // Голосовой вывод (Text-to-Speech)
  const speakText = useCallback((text: string, msgId: string) => {
    if (isSpeaking && speakingMsgId === msgId) {
      // Остановить
      window.speechSynthesis.cancel()
      setIsSpeaking(false)
      setSpeakingMsgId(null)
      return
    }

    // Очистить старые голоса
    window.speechSynthesis.cancel()

    const utterance = new SpeechSynthesisUtterance(text.replace(/[*#_\[\]()]/g, '').slice(0, 2000))
    utterance.lang = 'ru-RU'
    utterance.rate = 1.0
    utterance.pitch = 1.0

    // Используем выбранный голос
    const selected = getSelectedVoice()
    if (selected) {
      utterance.voice = selected
    } else {
      // Fallback: любой русский
      const voices = window.speechSynthesis.getVoices()
      const ruVoice = voices.find(v => v.lang.startsWith('ru'))
      if (ruVoice) utterance.voice = ruVoice
    }

    utterance.onstart = () => {
      setIsSpeaking(true)
      setSpeakingMsgId(msgId)
    }

    utterance.onend = () => {
      setIsSpeaking(false)
      setSpeakingMsgId(null)
    }

    utterance.onerror = () => {
      setIsSpeaking(false)
      setSpeakingMsgId(null)
    }

    window.speechSynthesis.speak(utterance)
  }, [isSpeaking, speakingMsgId, getSelectedVoice])

  const {
    chats,
    currentChat,
    isLoading,
    isSending,
    sendError,
    fetchChats,
    createChat,
    selectChat,
    sendMessage,
    sendMessageStream,
    setSending,
    deleteChat,
    clearCurrentChat,
    archiveChat,
    unarchiveChat,
  } = useChatStore()

  // Assign chat store values to voice dialog refs
  voiceDialogCurrentChatRef.current = currentChat
  voiceDialogSendMsgStreamRef.current = sendMessageStream

  const { balance, totalUsed, fetchBalance } = useTokenStore()

  useEffect(() => {
    fetchChats()
    fetchBalance()
  }, [fetchChats, fetchBalance])

  // Отфильтрованный список моделей (сортировка: бесплатные → платные)
  const hasTokens = (balance ?? 0) >= 1000
  const visibleModels = useMemo(() => {
    if (serverModels.length === 0) return []
    const filtered = !userModels
      ? serverModels
      : serverModels.filter(m => userModels.includes(m.model_name))
    const free = filtered.filter(m => !m.is_paid).map(toSelectorItem)
    const paid = filtered.filter(m => m.is_paid).map(toSelectorItem)
    return [...free, ...paid]
  }, [serverModels, userModels])

  useEffect(() => {
    if (id) {
      selectChat(id)
    } else {
      clearCurrentChat()
    }
  }, [id, selectChat, clearCurrentChat])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [currentChat?.messages])

  // При редактировании — заполняем input текущим текстом сообщения
  useEffect(() => {
    if (editMessageId && currentChat) {
      const msg = currentChat.messages.find((m) => m.id === editMessageId)
      if (msg) {
        // Берём текст без файловых вставок
        const clean = msg.content.split('\n---')[0].split('\n![')[0].trim()
        setInput(clean)
        setEditMessageContent(msg.content)
      }
    }
  }, [editMessageId])

  // Прикрепить файл
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return

    const newFiles: AttachedFile[] = []
    const maxSize = 10 * 1024 * 1024 // 10MB

    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      if (f.size > maxSize) {
        toast.error(`Файл ${f.name} слишком большой (макс 10MB)`)
        continue
      }
      newFiles.push({ name: f.name, base64: '', type: f.type })
    }

    // Читаем файлы
    Promise.all(
      newFiles.map((nf, idx) => new Promise<void>((resolve) => {
        // Берём оригинальный File из списка
        const file = files[idx]
        if (!file) { resolve(); return }
        const reader = new FileReader()
        reader.onload = () => {
          const result = reader.result as string
          nf.base64 = result.split(',')[1] || result
          resolve()
        }
        reader.onerror = () => resolve()
        reader.readAsDataURL(file)
      }))
    ).then(() => {
      setAttachedFiles((prev) => [...prev, ...newFiles])
    })

    // Сброс, чтобы можно было выбрать тот же файл снова
    e.target.value = ''
  }

  const removeFile = (idx: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== idx))
  }

  const [cameraModal, setCameraModal] = useState(false)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const handleCaptureCamera = () => {
    // Пробуем получить доступ к камере через getUserMedia
    if (navigator.mediaDevices?.getUserMedia) {
      setCameraModal(true)
      // Запустим камеру как только модалка отрендерится
      setTimeout(async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
            audio: false
          })
          streamRef.current = stream
          if (videoRef.current) {
            videoRef.current.srcObject = stream
          }
        } catch (err) {
          console.error('Camera error:', err)
          setCameraModal(false)
          toast.error('Не удалось получить доступ к камере')
        }
      }, 100)
    } else {
      // Фоллбэк: input capture
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.capture = 'environment'
      input.onchange = (e: any) => {
        if (e.target?.files?.length > 0) {
          handleFileSelect({ target: { files: e.target.files, value: '' } } as any)
        }
      }
      input.click()
    }
  }

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
    setCameraModal(false)
  }, [])

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return
    const video = videoRef.current
    const canvas = canvasRef.current
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(video, 0, 0)
    // Конвертируем в Blob
    canvas.toBlob((blob) => {
      if (!blob) return
      const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' })
      // Создаём фейковый event для handleFileSelect
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)
      const fakeEvent = {
        target: {
          files: dataTransfer.files,
          value: ''
        }
      } as any
      handleFileSelect(fakeEvent)
      stopCamera()
      toast.success('Фото добавлено')
    }, 'image/jpeg', 0.92)
  }

  const handleAddPageLink = () => {
    const url = window.prompt('Введите ссылку на страницу:', 'https://')
    if (!url || !url.trim()) return
    // Добавляем ссылку как текстовый файл (вставится в сообщение)
    setInput((prev) => {
      const trimmed = prev.trim()
      return trimmed ? `${trimmed}\n[Ссылка](${url.trim()})` : `[Ссылка](${url.trim()})`
    })
    inputRef.current?.focus()
  }

  const handleSend = async () => {
    const trimmed = input.trim()
    if ((!trimmed && attachedFiles.length === 0) || isSending) return

    // Проверка: если баланс 0, а выбрана платная модель — не отправляем
    const currentModel = serverModels.find(m => m.model_name === selectedModel)
    if (currentModel?.is_paid && (balance ?? 0) < 1000) {
      toast.error('Недостаточно токенов. Пополните баланс для использования платных моделей.')
      return
    }

    // Создаём AbortController для возможности отмены
    const controller = new AbortController()
    abortControllerRef.current = controller
    useChatStore.setState({ _abortSignal: controller.signal })

    // Режим редактирования — меняем содержимое сообщения на клиенте и переотправляем
    if (editMessageId) {
      if (!currentChat) { setEditMessageId(null); return }
      const updatedMsgs = currentChat.messages.map((m) =>
        m.id === editMessageId ? { ...m, content: trimmed } : m
      )
      useChatStore.setState({
        currentChat: { ...currentChat, messages: updatedMsgs },
      })
      setEditMessageId(null)
      setEditMessageContent('')
      setInput('')

      // Отправляем отредактированное сообщение как новое сообщение к AI
      await sendMessageStream(currentChat.id, trimmed, abortControllerRef.current!.signal, selectedModel, webSearch, attachedFiles, deepResearch)
      setAttachedFiles([])
      if (inputRef.current) {
        inputRef.current.style.height = 'auto'
      }
      return
    }

    let chat = currentChat
    if (!chat) {
      chat = await createChat(selectedModel)
    }

    await sendMessageStream(chat.id, trimmed, abortControllerRef.current!.signal, selectedModel, webSearch, attachedFiles, deepResearch)
    setInput('')
    setAttachedFiles([])
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
    }
  }

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    useChatStore.setState({ isSending: false })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
    // Escape — отмена редактирования
    if (e.key === 'Escape' && editMessageId) {
      setEditMessageId(null)
      setEditMessageContent('')
      setInput('')
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value)
    const textarea = e.target
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
  }

  const startNewChat = () => {
    clearCurrentChat()
    setInput('')
    setAttachedFiles([])
  }

  // Действия с сообщением
  const copyMessage = (content: string) => {
    navigator.clipboard.writeText(content)
    toast.success('Скопировано')
  }

  const deleteMessage = async (msgId: string) => {
    // Пока обрабатываем через store (если есть deleteMessage) или просто фильтруем через UI
    // Удаление на сервере — удалим сообщение
    try {
      const client = (await import('../api/client')).default
      await client.delete(`/chat/message/${msgId}`)
      toast.success('Сообщение удалено')
      // Перезагружаем чат
      if (currentChat?.id) {
        selectChat(currentChat.id)
      }
    } catch {
      toast.error('Ошибка при удалении')
    }
  }

  const regenerateMessage = () => {
    if (!currentChat || currentChat.messages.length < 2) return
    // Берём последнее сообщение пользователя и пересылаем
    const msgs = currentChat.messages
    let lastUserMsg: typeof msgs[0] | null = null
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === 'user') {
        lastUserMsg = msgs[i]
        break
      }
    }
    if (!lastUserMsg) return
    const content = typeof lastUserMsg.content === 'string' ? lastUserMsg.content : ''
    sendMessageStream(currentChat.id, content, abortControllerRef.current!.signal, selectedModel, webSearch, [], deepResearch)
  }

  const selectedModelData = visibleModels.find((m) => m.id === selectedModel)

  const handleExportSelected = async () => {
    if (selectedChatIds.size === 0) {
      toast.error('Выберите диалоги для экспорта')
      return
    }

    // Загружаем полные данные чатов с сообщениями
    const fullChats: ChatType[] = []
    for (const chatId of selectedChatIds) {
      try {
        const { data } = await client.get(`/chat/${chatId}`)
        fullChats.push(data)
      } catch (err) {
        console.error('Failed to load chat:', chatId)
      }
    }

    if (fullChats.length === 0) {
      toast.error('Не удалось загрузить диалоги')
      return
    }

    exportChats(fullChats)
    setSelectMode(false)
    setSelectedChatIds(new Set())
  }

  const handleExportSingleChat = async (chat: ChatType) => {
    try {
      // Загружаем полные данные чата с сообщениями
      const { data } = await client.get(`/chat/${chat.id}`)
      exportChats([data])
    } catch (err) {
      toast.error('Не удалось загрузить диалог для экспорта')
    }
  }

  const handleImportChats = () => {
    fileImportRef.current?.click()
  }

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    for (let i = 0; i < files.length; i++) {
      try {
        const count = await importChatsFromFile(files[i])
        if (count > 0) {
          toast.success(`Импортировано ${count} диалог(ов)`)
          fetchChats()
        } else {
          toast.error('Не удалось импортировать диалоги')
        }
      } catch (err: any) {
        toast.error(err.message || 'Ошибка импорта')
      }
    }

    e.target.value = ''
  }

  const toggleChatSelection = (chatId: string) => {
    setSelectedChatIds((prev) => {
      const next = new Set(prev)
      if (next.has(chatId)) {
        next.delete(chatId)
      } else {
        next.add(chatId)
      }
      return next
    })
  }

  // Отфильтрованные чаты по поиску
  const filteredChats = useMemo(() => {
    const activeChats = chats.filter((c: any) => !c.archived)
    if (!searchQuery) return activeChats
    const q = searchQuery.toLowerCase()
    return activeChats.filter((c: any) =>
      c.title.toLowerCase().includes(q) ||
      c.model.toLowerCase().includes(q)
    )
  }, [chats, searchQuery])

  // Определяем, картинка ли в сообщении
  const hasInlineImage = (content: string) => content.match(/!\[.*?\]\(data:image\//)

  return (
    <div className="flex h-full">
      {/* Chat area */}
      {/* Chat list sidebar */}
      <div id="chat-sidebar" className="w-72 border-r border-white/5 flex flex-col bg-[#0a0a15] hidden md:flex shrink-0" style={{ minWidth: '200px', maxWidth: '500px' }}>
        {/* Header with actions */}
        <div className="p-3 border-b border-white/5 space-y-2">
          <button
            onClick={startNewChat}
            className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl glass text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-all"
          >
            <Plus className="w-4 h-4" />
            Новый чат
          </button>

          {/* Поиск по истории чатов */}
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по истории чатов..."
              className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-xs text-gray-300 placeholder-gray-600 focus:outline-none focus:border-indigo-500/30 focus:bg-white/[0.07] transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-500 hover:text-gray-300 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Export/Import buttons row */}
          <div className="flex items-center gap-0.5 flex-wrap">
            {/* Всего чатов */}
            <span className="text-[10px] text-gray-500 mr-1.5 whitespace-nowrap hidden sm:inline">Всего чатов: {chats.length}</span>
            <button
              onClick={async (e) => {
                e.stopPropagation()
                if (!confirm('Очистить всю историю диалогов?')) return
                const ids = chats.map(c => c.id)
                // Удаляем каждый чат на сервере последовательно с задержкой
                for (let idx = 0; idx < ids.length; idx++) {
                  // Пропускаем первый — без задержки
                  if (idx > 0 && idx % 20 === 0) {
                    await new Promise(r => setTimeout(r, 1000))
                  }
                  try {
                    await client.delete(`/chat/${ids[idx]}`)
                  } catch (err: any) {
                    console.warn('clear history: delete failed', ids[idx], err?.response?.status)
                  }
                }
                // После удаления на сервере — обновляем список с сервера
                try {
                  const { data } = await client.get('/chat')
                  useChatStore.setState({ chats: data || [], currentChat: null })
                } catch {
                  useChatStore.setState({ chats: [], currentChat: null })
                }
              }}
              className="text-[10px] text-gray-500 hover:text-red-400 transition-all p-1 hover:bg-white/5 rounded-lg"
              title="Очистить историю"
            >
              <Trash2 className="w-3 h-3" />
            </button>
            {/* Select mode toggle — компактно на узких */}
            <button
              onClick={() => { setSelectMode(!selectMode); setSelectedChatIds(new Set()) }}
              className={'flex items-center justify-center gap-1 p-1.5 rounded-lg text-[10px] transition-all ' + (selectMode
                  ? 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/20'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-white/5')}
              title={selectMode ? 'Отменить выбор' : 'Выбрать диалоги'}
            >
              {selectMode ? <Square className="w-3.5 h-3.5" /> : <CheckSquare className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">Выбрать</span>
            </button>

            {/* Export selected */}
            {selectMode && selectedChatIds.size > 0 && (
              <button
                onClick={handleExportSelected}
                className="flex items-center justify-center gap-1 p-1.5 rounded-lg text-[10px] text-emerald-400 hover:bg-emerald-500/10 transition-all"
                title="Выгрузить выбранные"
              >
                <Download className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Выгрузить ({selectedChatIds.size})</span>
              </button>
            )}

            {/* Single chat export dropdown — компактно */}
            {!selectMode && (
              <div className="relative" style={{ maxWidth: '100%' }}>
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="flex items-center justify-center gap-1 p-1.5 rounded-lg text-[10px] text-gray-500 hover:text-gray-300 hover:bg-white/5 transition-all"
                  title="Экспорт/импорт"
                >
                  <FileDown className="w-3.5 h-3.5 shrink-0" />
                  <span className="hidden sm:inline whitespace-nowrap">Экспорт</span>
                </button>
                {showExportMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                    <div className="absolute top-full left-0 mt-1 w-44 glass-card z-20 p-1.5 space-y-0.5" style={{ left: 0, right: 'auto' }}>
                      {chats.filter(c => !c.archived).slice(0, 1).map(chat => (
                        <button
                          key={chat.id}
                          onClick={() => { handleExportSingleChat(chat); setShowExportMenu(false) }}
                          className="w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg text-[10px] text-gray-400 hover:text-gray-200 hover:bg-white/5 transition-all"
                        >
                          <Download className="w-3 h-3" />
                          Выгрузить текущий чат
                        </button>
                      ))}
                      <button
                        onClick={() => { handleImportChats(); setShowExportMenu(false) }}
                        className="w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg text-[10px] text-gray-400 hover:text-indigo-300 hover:bg-white/5 transition-all"
                      >
                        <Upload className="w-3 h-3" />
                        Импорт диалогов
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Hidden import file input */}
          <input
            ref={fileImportRef}
            type="file"
            onChange={handleFileImport}
            className="hidden"
            accept=".json"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {filteredChats.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-xs text-gray-600">
                {searchQuery ? 'Ничего не найдено' : 'Нет диалогов'}
              </p>
            </div>
          ) : (
            filteredChats.map((chat) => (
              <div key={chat.id} className="relative group">
                {selectMode ? (
                  /* Select mode: checkbox + preview */
                  <button
                    onClick={() => toggleChatSelection(chat.id)}
                    className={'w-full text-left px-3 py-2.5 rounded-xl text-sm transition-all flex items-center gap-2 ' + (selectedChatIds.has(chat.id)
                        ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/25'
                        : 'text-gray-400 hover:bg-white/5 hover:text-gray-200')}
                  >
                    <div className={'w-4 h-4 rounded border flex items-center justify-center shrink-0 ' + (selectedChatIds.has(chat.id)
                        ? 'bg-indigo-500 border-indigo-400 text-white'
                        : 'border-gray-600')}>
                      {selectedChatIds.has(chat.id) && (
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs truncate block">{chat.title}</span>
                      <span className="text-[10px] text-gray-600 mt-0.5 block">{chat.model}</span>
                    </div>
                  </button>
                ) : (
                  /* Normal mode: click to open + hover actions */
                  <button
                    onClick={() => {
                      selectChat(chat.id)
                      navigate('/chat/' + chat.id)
                    }}
                    className={'w-full text-left px-3 py-2.5 rounded-xl text-sm transition-all ' + (currentChat?.id === chat.id
                        ? 'bg-indigo-500/10 text-indigo-300 border border-indigo-500/20'
                        : 'text-gray-400 hover:bg-white/5 hover:text-gray-200')}
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate flex-1">{chat.title}</span>
                      <div className="flex items-center gap-1">
                        {/* Export single chat */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleExportSingleChat(chat) }}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:text-emerald-400 transition-all"
                          title="Выгрузить историю чата"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        {/* Share chat */}
                        <div className="relative opacity-0 group-hover:opacity-100">
                          <button
                            onClick={(e) => { e.stopPropagation(); setShowShareMenu(showShareMenu === chat.id ? null : chat.id) }}
                            className="p-1 hover:text-indigo-400 transition-all"
                            title="Поделиться"
                          >
                            <Share2 className="w-3.5 h-3.5" />
                          </button>
                          {showShareMenu === chat.id && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setShowShareMenu(null) }} />
                              <div className="absolute top-0 right-full mr-2 w-36 glass-card z-20 p-1.5 space-y-0.5">
                                <button
                                  onClick={(e) => { e.stopPropagation(); copyShareLink(chat.id); setShowShareMenu(null) }}
                                  className="w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg text-[10px] text-gray-400 hover:text-gray-200 hover:bg-white/5 transition-all"
                                >
                                  <Copy className="w-3 h-3" />
                                  Копировать ссылку
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); shareToSocial('telegram', chat.id); setShowShareMenu(null) }}
                                  className="w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg text-[10px] text-gray-400 hover:text-blue-400 hover:bg-white/5 transition-all"
                                >
                                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.127.037.414.037.414l-1.398 6.578c-.116.544-.49.676-.817.424l-3.2-2.356-.883.855c-.127.126-.25.246-.477.246-.244 0-.395-.085-.514-.3l-.952-3.13-2.328-.77c-.558-.2-.558-.558 0-.806l10.137-3.898c.3-.113.552-.076.687.006.12.073.184.19.2.334z"/></svg>
                                  Telegram
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); shareToSocial('whatsapp', chat.id); setShowShareMenu(null) }}
                                  className="w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg text-[10px] text-gray-400 hover:text-green-400 hover:bg-white/5 transition-all"
                                >
                                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                  WhatsApp
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); shareToSocial('vkontakte', chat.id); setShowShareMenu(null) }}
                                  className="w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg text-[10px] text-gray-400 hover:text-blue-600 hover:bg-white/5 transition-all"
                                >
                                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M15.684 0H8.316C2.88 0 0 2.88 0 8.316v7.368C0 21.12 2.88 24 8.316 24h7.368C21.12 24 24 21.12 24 15.684V8.316C24 2.88 21.12 0 15.684 0zm3.6 16.956h-1.488c-.576 0-.756-.444-1.764-1.452-.876-.828-1.284-.936-1.5-.936-.324 0-.408.108-.408.648v1.104c0 .408-.12.624-1.188.624-1.764 0-3.708-1.08-5.076-3.024-1.776-2.34-2.34-4.092-2.34-4.464 0-.216.108-.408.504-.408h1.512c.384 0 .528.168.672.576.756 2.196 1.992 4.104 2.508 4.104.192 0 .276-.108.276-.696v-2.508c0-1.104-.648-1.2-.648-1.596 0-.18.144-.348.372-.348h2.364c.312 0 .42.168.42.54v2.916c0 .312.144.42.252.42.204 0 .372-.108.744-.48.756-.852 1.308-2.184 1.308-2.184.072-.18.18-.348.468-.348h1.512c.504 0 .612.252.504.588-.192.888-2.052 3.072-2.052 3.072-.168.216-.204.324 0 .576.144.204.648.636.984 1.02.552.624.984 1.14.984 1.452.012.432-.216.648-.624.648z"/></svg>
                                  VK
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); archiveChat(chat.id) }}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:text-amber-400 transition-all"
                          title="В архив"
                        >
                          <Archive className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteChat(chat.id) }}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-400 transition-all"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-gray-600">{chat.model}</span>
                      <span className="text-[10px] text-gray-500">
                        {chat.tokensUsed || 0} токенов
                      </span>
                    </div>
                  </button>
                )}
              </div>
            ))
          )}
        </div>



        {/* Archived chats — collapsible block at bottom */}
        {chats.filter(c => c.archived).length > 0 && (
          <div className="border-t border-white/5 bg-[#0a0a15]">
            <button
              onClick={() => setArchiveOpen(!archiveOpen)}
              className="w-full flex items-center gap-1.5 px-3 py-2 text-[10px] text-gray-600 font-medium hover:bg-white/5 transition-all"
            >
              <Archive className="w-3 h-3" />
              <span>Архив</span>
              <span className="ml-1 text-[10px] text-gray-600">({chats.filter(c => c.archived).length})</span>
              <svg
                className={'w-3 h-3 ml-auto transition-transform ' + (archiveOpen ? 'rotate-180' : '')}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {archiveOpen && (
              <div className="px-2 pb-2 space-y-1">
                {chats.filter(c => c.archived).map((chat) => (
                  <button
                    key={chat.id}
                    onClick={() => {
                      unarchiveChat(chat.id)
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg text-xs transition-all group flex items-center justify-between text-gray-500 hover:text-gray-300 hover:bg-white/5"
                  >
                    <span className="truncate flex-1">{chat.title}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteChat(chat.id) }}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-400 transition-all"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Resizable divider */}
      <div
        className="w-1.5 cursor-col-resize hover:bg-indigo-500/30 active:bg-indigo-500/50 transition-colors shrink-0 relative hidden md:block"
        onMouseDown={handleResizeStart}
      />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto">
          {currentChat && currentChat.messages.length > 0 ? (
            <div className="max-w-4xl mx-auto px-6 py-6 space-y-4">
              {currentChat.messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`group relative flex gap-3 ${
                    msg.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                  onMouseEnter={() => setHoveredMsgId(msg.id)}
                  onMouseLeave={() => setHoveredMsgId(null)}
                >
                  {msg.role === 'assistant' && (
                    <div className="flex-shrink-0 mt-1">
                      {aiModels[msg.model || '']?.icon_url ? (
                        <img
                          src={aiModels[msg.model || ''].icon_url!}
                          alt=""
                          className="w-8 h-8 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  )}

                  <div
                    className={`
                      max-w-full px-5 py-4 rounded-2xl relative
                      ${msg.role === 'user'
                        ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-tr-sm chat-message-user'
                        : 'glass text-gray-200 rounded-tl-sm chat-message'
                      }
                    `}
                  >
                    {msg.role === 'assistant' ? (
                      <>
                        <div className="text-[10px] font-medium text-indigo-400/80 mb-1 flex items-center gap-1.5">
                          {msg.model || 'GPT-Chatbot'}
                          {isSending && msg.content === '' && (
                            <span className="inline-flex items-center gap-0.5">
                              <span className="w-1.5 h-1.5 bg-indigo-400/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                              <span className="w-1.5 h-1.5 bg-indigo-400/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                              <span className="w-1.5 h-1.5 bg-indigo-400/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                            </span>
                          )}
                        </div>
                        <AssistantMessage content={msg.content} />
                      </>
                    ) : (
                      <div className="text-sm whitespace-pre-wrap">
                        {/* Если есть файлы с изображениями — показываем их */}
                        {msg.content.split('\n').map((line, i) => {
                          const imgMatch = line.match(/^\[(.+?)\]\(data:image\/(\w+);base64,(.+)\)$/)
                          if (imgMatch) {
                            return (
                              <span key={i}>
                                <img
                                  src={`data:image/${imgMatch[2]};base64,${imgMatch[3]}`}
                                  alt={imgMatch[1]}
                                  className="max-w-full rounded-lg my-1 max-h-64 object-contain"
                                />
                                <br />
                              </span>
                            )
                          }
                          const fileMatch = line.match(/^--- (.+?) ---/)
                          if (fileMatch) {
                            return (
                              <span key={i} className="flex items-center gap-1 text-indigo-300 text-xs">
                                <FileText className="w-3 h-3" /> {fileMatch[1]}
                                <br />
                              </span>
                            )
                          }
                          return <span key={i}>{line}<br /></span>
                        })}
                      </div>
                    )}



                    {/* Action buttons on hover */}
                    {hoveredMsgId === msg.id && (
                      <div
                        className={`
                          absolute -top-2 right-0 flex items-center gap-0.5
                          ${msg.role === 'user' ? '-translate-x-0' : '-translate-x-0'}
                        `}
                      >
                        {/* Edit — только для user */}
                        {msg.role === 'user' && (
                          <button
                            onClick={() => {
                              setEditMessageId(msg.id)
                              setEditMessageContent(msg.content)
                              inputRef.current?.focus()
                            }}
                            className="p-1 rounded-md bg-gray-800 border border-white/10 text-gray-400 hover:text-white hover:bg-gray-700 transition-all"
                            title="Редактировать"
                          >
                            <Edit3 className="w-3 h-3" />
                          </button>
                        )}

                        {/* Copy */}
                        <button
                          onClick={() => copyMessage(msg.content)}
                          className="p-1 rounded-md bg-gray-800 border border-white/10 text-gray-400 hover:text-white hover:bg-gray-700 transition-all"
                          title="Копировать"
                        >
                          <Copy className="w-3 h-3" />
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => deleteMessage(msg.id)}
                          className="p-1 rounded-md bg-gray-800 border border-white/10 text-red-400 hover:text-red-300 hover:bg-red-900/30 transition-all"
                          title="Удалить"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>

                        {/* Regenerate — только для assistant */}
                        {msg.role === 'assistant' && (
                          <button
                            onClick={regenerateMessage}
                            className="p-1 rounded-md bg-gray-800 border border-white/10 text-gray-400 hover:text-white hover:bg-gray-700 transition-all"
                            title="Перегенерировать"
                          >
                            <RefreshCw className="w-3 h-3" />
                          </button>
                        )}

                        {/* Speak — только для assistant */}
                        {msg.role === 'assistant' && msg.content && (
                          <button
                            onClick={() => speakText(msg.content, msg.id)}
                            className={`p-1 rounded-md border transition-all ${
                              isSpeaking && speakingMsgId === msg.id
                                ? 'bg-indigo-500/20 border-indigo-500/30 text-indigo-400'
                                : 'bg-gray-800 border-white/10 text-gray-400 hover:text-white hover:bg-gray-700'
                            }`}
                            title={isSpeaking && speakingMsgId === msg.id ? 'Остановить' : 'Озвучить ответ'}
                          >
                            {isSpeaking && speakingMsgId === msg.id ? (
                              <StopCircle className="w-3 h-3" />
                            ) : (
                              <Volume2 className="w-3 h-3" />
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {msg.role === 'user' && (
                    <div className="flex-shrink-0 mt-1">
                      {user?.avatar_url ? (
                        <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-lg object-cover border border-indigo-500/30" />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center">
                          <User className="w-4 h-4 text-indigo-300" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {!isSending && sendError && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                    </div>
                  </div>
                  <div className="bg-red-500/5 border border-red-500/20 px-5 py-4 rounded-2xl rounded-tl-sm max-w-full">
                    <p className="text-sm text-red-300 whitespace-pre-wrap">{sendError}</p>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center h-full min-h-[60vh]">
              <div className="text-center max-w-md px-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mx-auto mb-6 animate-float">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-xl font-bold gradient-text mb-2">Нейрочат онлайн</h1>
                <p className="text-sm text-gray-500 mb-6">
                  Выберите ИИ-модель и начните диалог. GPT, DeepSeek, Claude, Gemini и другие модели в одном интерфейсе.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="border-t border-white/5 bg-[#0f0f1a]">
          <div className="max-w-4xl mx-auto px-6 py-3">
            {/* Model selector */}
            <div className="relative mb-2">
              <button
                onClick={() => setShowModelSelector(!showModelSelector)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg glass text-xs text-gray-400 hover:text-gray-200 transition-colors"
              >
                <Bot className="w-3 h-3" />
                {selectedModelData?.icon === "FREE" ? (
                  <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">FREE</span>
                ) : (
                  <span>{selectedModelData?.icon}</span>
                )}
                <span>{selectedModelData?.name}</span>
                <span className="text-gray-600">·</span>
                <span className={selectedModelData?.color}>{selectedModelData?.provider}</span>
              </button>

              {showModelSelector && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowModelSelector(false)} />
                  <div className="absolute bottom-full left-0 mb-2 w-96 glass-card z-20 p-2 space-y-1 max-h-80 overflow-y-auto">
                    {visibleModels.map((model) => {
                      const isPaidLocked = model.is_paid && !hasTokens
                      return (
                        <button
                          key={model.id}
                          onClick={() => {
                            if (isPaidLocked) {
                              toast.error('Недостаточно токенов. Пополните баланс для доступа к платным моделям.')
                              return
                            }
                            setSelectedModel(model.id); setShowModelSelector(false)
                          }}
                          className={`
                            w-full text-left px-3 py-2 rounded-lg text-sm transition-colors
                            ${selectedModel === model.id
                              ? 'bg-indigo-500/10 text-indigo-300'
                              : isPaidLocked
                                ? 'text-gray-500 cursor-not-allowed'
                                : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                            }
                          `}
                        >
                          <div className="flex items-center justify-between">
                            <span className="flex items-center gap-1.5">
                              {model.icon === "FREE" ? (
                                <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">FREE</span>
                              ) : (
                                <span>{model.icon}</span>
                              )}
                              <span className={`${model.color} ${isPaidLocked ? 'opacity-50' : ''}`}>{model.name}</span>
                            </span>
                            <span className="flex items-center gap-2">
                              {isPaidLocked && (
                                <span className="text-[9px] text-amber-400/70 font-medium">🔒 Пополнить</span>
                              )}
                              {!isPaidLocked && model.priceText && (
                                <span className="text-[10px] text-indigo-400/70 font-mono">{model.priceText}</span>
                              )}
                              <span className="text-xs text-gray-600">{model.provider}</span>
                            </span>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Edit mode indicator */}
            {editMessageId && (
              <div className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                <Edit3 className="w-3.5 h-3.5 text-indigo-400" />
                <span className="text-xs text-indigo-300">Редактирование сообщения</span>
                <button
                  onClick={() => { setEditMessageId(null); setEditMessageContent(''); setInput('') }}
                  className="ml-auto p-0.5 rounded hover:bg-indigo-500/20 text-indigo-400"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Attached files preview */}
            {attachedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {attachedFiles.map((f, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-xs text-indigo-300"
                  >
                    {f.type.startsWith('image/') ? (
                      <Image className="w-3 h-3" />
                    ) : (
                      <FileText className="w-3 h-3" />
                    )}
                    <span className="max-w-[120px] truncate">{f.name}</span>
                    <button
                      onClick={() => removeFile(idx)}
                      className="p-0.5 hover:text-red-400 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Input field */}
            <div className="glass rounded-2xl p-1.5 border border-white/5 focus-within:border-indigo-500/30 transition-all">
              <div className="flex items-end gap-2">
                {/* Voice input button — popup with two options */}
                <div className="relative">
                  <button
                    onClick={() => setShowVoicePopup(prev => !prev)}
                    className={`p-2 rounded-lg transition-all shrink-0 ${
                      isRecording || voiceDialogOpen
                        ? 'bg-red-500/20 text-red-400 animate-pulse'
                        : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                    }`}
                    title="Голосовой ввод"
                  >
                    {isRecording || voiceDialogOpen ? <StopCircle className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>

                  {showVoicePopup && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowVoicePopup(false)} />
                      <div className="absolute bottom-full left-0 mb-2 w-56 bg-[#1a1a2e] border border-white/10 rounded-xl z-20 p-1.5 space-y-0.5 shadow-xl">
                        <button
                          onClick={() => {
                            setShowVoicePopup(false)
                            startRecording()
                          }}
                          className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-all"
                        >
                          <Mic className="w-4 h-4 text-indigo-400" />
                          <span>Ввести голосом</span>
                        </button>
                        <button
                          onClick={startVoiceDialog}
                          className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-all"
                        >
                          <span className="text-lg">🗣️</span>
                          <span>Голосовой диалог</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Attach file button with menu */}
                <div className="relative">
                  <button
                    onClick={() => setShowAttachMenu(prev => !prev)}
                    className="p-2 rounded-lg text-gray-500 hover:text-gray-300 hover:bg-white/5 transition-all shrink-0"
                    title="Прикрепить"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>

                  {showAttachMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowAttachMenu(false)} />
                      <div className="absolute bottom-full left-0 mb-2 w-52 bg-[#1a1a2e] border border-white/10 rounded-xl z-20 p-1.5 space-y-0.5 shadow-xl">
                        <button
                          onClick={() => { fileInputRef.current?.click(); setShowAttachMenu(false) }}
                          className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-all"
                        >
                          <Upload className="w-4 h-4 text-indigo-400" />
                          <span>Загрузить файлы</span>
                        </button>
                        <button
                          onClick={() => { handleCaptureCamera(); setShowAttachMenu(false) }}
                          className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-all"
                        >
                          <Camera className="w-4 h-4 text-purple-400" />
                          <span>Захват камерой</span>
                        </button>
                        <button
                          onClick={() => { handleAddPageLink(); setShowAttachMenu(false) }}
                          className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-all"
                        >
                          <Link className="w-4 h-4 text-blue-400" />
                          <span>Добавить ссылку на страницу</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  multiple
                  accept="*/*"
                />

                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Напишите сообщение..."
                  rows={1}
                  className="flex-1 bg-transparent px-1 py-2 text-sm text-gray-200 placeholder-gray-600 resize-none max-h-[260px] outline-none"
                />

                <div className="flex items-center gap-1 pb-1 pr-1">
                  {/* Deep Research toggle */}
                  <div className="relative">
                    <button
                      onClick={() => {
                        if (deepResearch) {
                          setDeepResearch(false)
                        } else {
                          setShowDeepResearchPopup(true)
                        }
                      }}
                      className={`
                        p-2 rounded-lg transition-all
                        ${deepResearch
                          ? 'bg-amber-500/20 text-amber-400'
                          : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                        }
                      `}
                      title="Deep Research"
                    >
                      <span className="text-xs">🔬</span>
                    </button>
                    {showDeepResearchPopup && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowDeepResearchPopup(false)} />
                        <div className="absolute bottom-full right-0 mb-2 w-72 bg-gray-900/95 backdrop-blur-none z-50 p-3 rounded-xl shadow-xl border border-white/10">
                          <p className="text-[11px] text-gray-300 leading-relaxed">
                            <strong>Deep Research</strong><br />
                            <span className="text-gray-400">
                              AI выполнит глубокий анализ с поиском по многим источникам, 
                              прочитает несколько страниц и выдаст структурированный отчёт.
                              Это может занять 30–60 секунд.
                            </span>
                          </p>
                          {selectedModel && selectedModel.includes('/') && (
                            <div className="mt-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                              <p className="text-[10px] text-amber-300/80 leading-relaxed">
                                Выбрана платная модель. Deep Research выполняет до 3 итераций поиска, на каждую
                                уходит несколько AI-запросов. Итоговый расход токенов может быть
                                в <strong>5-10 раз выше</strong>, чем у обычного сообщения.
                              </p>
                            </div>
                          )}
                          <button
                            onClick={() => {
                              setWebSearch(false)
                              setDeepResearch(true)
                              setShowDeepResearchPopup(false)
                            }}
                            className="mt-2 w-full px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-400 text-[11px] font-medium hover:bg-amber-500/30 transition-all"
                          >
                            Включить
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      if (webSearch) {
                        setWebSearch(false)
                      } else {
                        setDeepResearch(false)
                        setWebSearch(true)
                      }
                    }}
                    className={`
                      p-2 rounded-lg transition-all
                      ${webSearch
                        ? 'bg-indigo-500/20 text-indigo-400'
                        : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                      }
                    `}
                    title="Поиск в интернете"
                  >
                    <Globe className="w-4 h-4" />
                  </button>

                  {isSending ? (
                    <button
                      onClick={handleStop}
                      className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
                      title="Остановить"
                    >
                      <StopCircle className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={handleSend}
                      disabled={!input.trim() && attachedFiles.length === 0}
                      className="p-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-500 hover:to-purple-500 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Status bar */}
            <div className="flex items-center justify-between mt-2 px-1">
              <div className="flex items-center gap-2 text-[10px] text-gray-600">
                <div className="relative flex items-center gap-0">
                  <button
                    onClick={() => setShowSettingsPanel(!showSettingsPanel)}
                    className="p-1 rounded-md text-gray-500 hover:text-indigo-400 hover:bg-white/5 transition-all"
                    title="Настройки чата"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowVoiceMenu(!showVoiceMenu)}
                    className="p-1 rounded-md text-gray-500 hover:text-indigo-400 hover:bg-white/5 transition-all"
                    title="Голос озвучки"
                  >
                    <Volume2 className="w-4 h-4" />
                  </button>
                  {showSettingsPanel && (
                    <ChatSettingsPanel
                      onImport={handleImportChats}
                      onClose={() => setShowSettingsPanel(false)}
                    />
                  )}
                  {showVoiceMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowVoiceMenu(false)} />
                      <div className="absolute bottom-full left-0 mb-1 w-56 glass-card z-20 p-1.5 space-y-0.5 max-h-48 overflow-y-auto">
                        {availableVoices.length === 0 ? (
                          <div className="px-2 py-1.5 text-[10px] text-gray-500">Нет голосов</div>
                        ) : (
                          availableVoices.map((v) => (
                            <button
                              key={v.name}
                              onClick={() => {
                                setSelectedVoiceName(v.name)
                                setShowVoiceMenu(false)
                              }}
                              className={`w-full text-left px-2 py-1.5 rounded-md text-[10px] transition-colors ${
                                selectedVoiceName === v.name
                                  ? 'bg-indigo-500/10 text-indigo-300'
                                  : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="truncate flex-1">{v.name}</span>
                                <span className="text-[8px] text-gray-600 ml-1">{v.lang}</span>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
                {deepResearch && (
                  <>
                    <span>·</span>
                    <span className="text-xs">🔬</span>
                    <span>Режим исследования</span>
                  </>
                )}
                {!deepResearch && webSearch && (
                  <>
                    <span>·</span>
                    <Globe className="w-3 h-3" />
                    <span>Поиск вкл</span>
                  </>
                )}
                {attachedFiles.length > 0 && (
                  <>
                    <span>·</span>
                    <Paperclip className="w-3 h-3" />
                    <span>{attachedFiles.length} файл(ов)</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2 text-[10px] text-gray-500">
                <span className="text-gray-500">остаток:</span>
                <span className="text-amber-400/80 font-medium">{balance.toLocaleString()}</span>
                <span className="text-gray-600">токенов</span>
              </div>
            </div>
          </div>
        </div>

        {/* Camera modal */}
        {cameraModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={stopCamera}>
            <div className="relative rounded-2xl overflow-hidden max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full rounded-2xl bg-black max-h-[70vh] object-contain"
              />
              <canvas ref={canvasRef} className="hidden" />
              <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
                <button
                  onClick={takePhoto}
                  className="px-8 py-3 rounded-full bg-white text-gray-900 font-semibold hover:bg-gray-200 transition-all shadow-lg text-sm"
                >
                  📸 Сделать снимок
                </button>
                <button
                  onClick={stopCamera}
                  className="px-6 py-3 rounded-full bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-all text-sm backdrop-blur-sm"
                >
                  ✕ Отмена
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Voice Dialog Panel */}
        {voiceDialogOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop blur */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={stopVoiceDialog} />
            
            {/* Panel */}
            <div className="relative w-full max-w-lg mx-4 glass rounded-3xl p-8 flex flex-col items-center" style={{ minHeight: '400px', maxHeight: '80vh' }}>
              {/* Close button */}
              <button
                onClick={stopVoiceDialog}
                className="absolute top-4 right-4 p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/5 transition-all"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Status indicator */}
              <div className="flex flex-col items-center gap-4 mt-4">
                {/* Pulsing microphone */}
                <div className="relative">
                  <div className={`
                    w-24 h-24 rounded-full flex items-center justify-center
                    ${voiceDialogStatus === 'listening' 
                      ? 'voice-pulse bg-red-500/20 border-2 border-red-400/50' 
                      : voiceDialogStatus === 'thinking'
                        ? 'voice-pulse-thinking bg-amber-500/20 border-2 border-amber-400/50'
                        : voiceDialogStatus === 'speaking'
                          ? 'voice-pulse-speaking bg-indigo-500/20 border-2 border-indigo-400/50'
                          : 'bg-gray-500/20 border-2 border-gray-400/30'
                    }
                  `}>
                    <Mic className={`w-10 h-10 ${
                      voiceDialogStatus === 'listening' ? 'text-red-400' :
                      voiceDialogStatus === 'thinking' ? 'text-amber-400' :
                      voiceDialogStatus === 'speaking' ? 'text-indigo-400' :
                      'text-gray-400'
                    }`} />
                  </div>
                </div>

                {/* Status text */}
                <div className="text-center">
                  <p className="text-lg font-medium text-gray-200">
                    {voiceDialogStatus === 'listening' && '🎤 Слушаю...'}
                    {voiceDialogStatus === 'thinking' && '💭 Думаю...'}
                    {voiceDialogStatus === 'speaking' && '🔊 Отвечаю...'}
                    {voiceDialogStatus === 'idle' && 'Готов'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {voiceDialogStatus === 'listening' && 'Говорите на русском языке'}
                    {voiceDialogStatus === 'thinking' && 'AI обрабатывает ваш запрос'}
                    {voiceDialogStatus === 'speaking' && 'Воспроизвожу ответ'}
                  </p>
                </div>
              </div>

              {/* Transcript */}
              <div className="flex-1 w-full mt-6 overflow-y-auto space-y-3 px-1" style={{ maxHeight: '250px' }}>
                {voiceDialogTranscript.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-600">Начните говорить, чтобы увидеть диалог</p>
                  </div>
                ) : (
                  voiceDialogTranscript.map((entry, idx) => (
                    <div
                      key={idx}
                      className={`flex gap-2 ${entry.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm ${
                          entry.role === 'user'
                            ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-tr-sm'
                            : 'glass text-gray-300 rounded-tl-sm'
                        }`}
                      >
                        <p className="text-[10px] font-medium opacity-60 mb-0.5">
                          {entry.role === 'user' ? 'Вы' : 'AI'}
                        </p>
                        {entry.role === 'assistant' ? (
                          <ReactMarkdown
                            components={markdownComponents}
                            className="text-sm leading-relaxed prose prose-invert max-w-none prose-p:my-1 prose-headings:my-2 prose-strong:text-indigo-300 prose-strong:font-semibold"
                          >
                            {preprocessMath(entry.text)}
                          </ReactMarkdown>
                        ) : (
                          <p className="text-sm leading-relaxed">{entry.text}</p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Actions */}
              <div className="mt-4 flex gap-3 w-full">
                {voiceDialogStatus === 'listening' && (
                  <button
                    onClick={stopVoiceDialog}
                    className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-red-300 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 transition-all"
                  >
                    <StopCircle className="w-4 h-4 inline mr-1.5" />
                    Завершить
                  </button>
                )}
                {(voiceDialogStatus === 'thinking' || voiceDialogStatus === 'speaking') && (
                  <button
                    onClick={stopVoiceDialog}
                    className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-400 bg-white/5 border border-white/10 hover:bg-white/10 transition-all"
                  >
                    Прервать
                  </button>
                )}
                <button
                  onClick={saveVoiceDialogToChat}
                  disabled={voiceDialogTranscript.length === 0}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-indigo-300 bg-indigo-500/10 border border-indigo-500/20 hover:bg-indigo-500/20 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4 inline mr-1.5" />
                  Сохранить в чат
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Voice Dialog CSS Animations */}
        <style>{`
          @keyframes voice-pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.8;
            }
            50% {
              transform: scale(1.08);
              opacity: 1;
            }
          }
          @keyframes voice-pulse-ring {
            0% {
              transform: scale(1);
              opacity: 0.4;
            }
            100% {
              transform: scale(1.3);
              opacity: 0;
            }
          }
          .voice-pulse {
            animation: voice-pulse 1.2s ease-in-out infinite !important;
            box-shadow: 0 0 30px rgba(239, 68, 68, 0.3);
          }
          .voice-pulse::before {
            content: '';
            position: absolute;
            inset: -8px;
            border-radius: 50%;
            border: 2px solid rgba(239, 68, 68, 0.3);
            animation: voice-pulse-ring 1.5s ease-out infinite;
          }
          .voice-pulse-thinking {
            animation: voice-pulse 1.5s ease-in-out infinite !important;
            box-shadow: 0 0 30px rgba(251, 191, 36, 0.3);
          }
          .voice-pulse-thinking::before {
            content: '';
            position: absolute;
            inset: -8px;
            border-radius: 50%;
            border: 2px solid rgba(251, 191, 36, 0.3);
            animation: voice-pulse-ring 1.8s ease-out infinite;
          }
          .voice-pulse-speaking {
            animation: voice-pulse 1.0s ease-in-out infinite !important;
            box-shadow: 0 0 30px rgba(99, 102, 241, 0.4);
          }
          .voice-pulse-speaking::before {
            content: '';
            position: absolute;
            inset: -8px;
            border-radius: 50%;
            border: 2px solid rgba(99, 102, 241, 0.4);
            animation: voice-pulse-ring 1.2s ease-out infinite;
          }
        `}</style>
        
      </div>
    </div>
  )
}
